import { gql } from "generated-graphql";

export const HARMONIZED_VIOLATIONS = gql(`
  query HarmonizedViolations(
    $tenantId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    harmonizedViolations(
      tenantId: $tenantId
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      count
      items {
        id
        violationId
        epaFacilityId
        registryId
        state
        sourceProgram
        violationCode
        violationDescription
        standardizedSeverity
        stdSeverityDeterminantCode
        stdSeverityDeterminantDesc
        orderStatuteCode
        standardizedResolutionStatus
        govStatus
        duplicateOfId
        duplicateCount
        duplicateOf {
          id
          violationId
          violationDescription
          facility {
            id
            name
          }
        }
        resolutionMethodDesc
        scheduleEventDesc
        pollutantTypeDesc
        citations
        citationDescs
        violationAgencyLevel
        violationAgency
        epaFacilityLink
        detailLink
        violationStartDate
        violationEndDate
        permitTerminalDate
        priorOccurrenceCount
        matchProvenance
        lastSeenAt
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        staffAssignee {
          id
          email
          person {
            first
            last
          }
        }
        facility {
          id
          name
          businessUnit
          department
        }
        siteCount
        isColocationPrimary
        affectedFacilities {
          id
          name
        }
        lastEditedAt
        lastEditedBy {
          id
          email
          person {
            first
            last
          }
        }
        heldMatch {
          id
          matchKind
          altIdValue
          violationCount
          acceptedAt
          rejectedAt
        }
        fieldNotes {
          ...ViolationFieldNotes
        }
        fieldNotesAvailable
      }
    }
  }
`);

/**
 * The duplicate picker's own read path, not the grid query: the grid collapses
 * co-located rows to one per violation id and so cannot order on facility at
 * all, which is exactly what the picker needs. `facilityId` orders the results
 * (the subject's own facility leads) rather than narrowing them; `program`
 * narrows. Selects only what an option label and the different-period check
 * read, since the grid's stats and affected-facility work is all wasted here.
 */
export const HARMONIZED_VIOLATION_DUPLICATE_CANDIDATES = gql(`
  query HarmonizedViolationDuplicateCandidates(
    $tenantId: ID!
    $program: HarmonizedViolationProgram
    $facilityId: ID
    $search: String
    $page: Int
    $pageSize: Int
  ) {
    harmonizedViolationDuplicateCandidates(
      tenantId: $tenantId
      program: $program
      facilityId: $facilityId
      search: $search
      page: $page
      pageSize: $pageSize
    ) {
      count
      items {
        id
        violationId
        violationDescription
        violationStartDate
        sourceProgram
        facility {
          id
          name
        }
      }
    }
  }
`);

/**
 * The violations marked as duplicates of one canonical, for the drawer's
 * Duplicate section. Drawer-local like the comment thread rather than a field on
 * the grid query, which selects `duplicateCount` on every row and would fetch a
 * list per row. Selects exactly what a label reads; `limit` caps the preview,
 * and `count` is the whole set, so the section can say how many it left out.
 */
export const HARMONIZED_VIOLATION_DUPLICATES = gql(`
  query HarmonizedViolationDuplicates($tenantId: ID!, $canonicalId: ID!, $limit: Int) {
    harmonizedViolationDuplicates(tenantId: $tenantId, canonicalId: $canonicalId, limit: $limit) {
      count
      items {
        id
        violationId
        violationDescription
        facility {
          id
          name
        }
      }
    }
  }
`);

export const SET_HARMONIZED_VIOLATION_GOV_STATUS = gql(`
  mutation SetHarmonizedViolationGovStatus(
    $tenantId: ID!
    $id: ID!
    $govStatus: HarmonizedViolationGovStatus
    $closureReason: String
  ) {
    setHarmonizedViolationGovStatus(
      tenantId: $tenantId
      id: $id
      govStatus: $govStatus
      closureReason: $closureReason
    ) {
      id
      govStatus
      lastEditedAt
      lastEditedBy {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

export const SET_HARMONIZED_VIOLATION_DUPLICATE_OF = gql(`
  mutation SetHarmonizedViolationDuplicateOf($tenantId: ID!, $id: ID!, $duplicateOfId: ID) {
    setHarmonizedViolationDuplicateOf(tenantId: $tenantId, id: $id, duplicateOfId: $duplicateOfId) {
      id
      duplicateOfId
      duplicateOf {
        id
        violationId
        violationDescription
        facility {
          id
          name
        }
      }
    }
  }
`);

export const SET_HARMONIZED_VIOLATIONS_DUPLICATE_OF = gql(`
  mutation SetHarmonizedViolationsDuplicateOf($tenantId: ID!, $ids: [ID!]!, $duplicateOfId: ID) {
    setHarmonizedViolationsDuplicateOf(tenantId: $tenantId, ids: $ids, duplicateOfId: $duplicateOfId) {
      id
      duplicateOfId
    }
  }
`);

export const SET_HARMONIZED_VIOLATION_ASSIGNEE = gql(`
  mutation SetHarmonizedViolationAssignee($tenantId: ID!, $id: ID!, $userId: ID) {
    setHarmonizedViolationAssignee(tenantId: $tenantId, id: $id, userId: $userId) {
      id
      assignee {
        id
        email
        person {
          first
          last
        }
      }
      lastEditedAt
      lastEditedBy {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

export const SET_HARMONIZED_VIOLATION_ASSIGNEES = gql(`
  mutation SetHarmonizedViolationAssignees($tenantId: ID!, $ids: [ID!]!, $userId: ID) {
    setHarmonizedViolationAssignees(tenantId: $tenantId, ids: $ids, userId: $userId) {
      id
      assignee {
        id
        email
        person {
          first
          last
        }
      }
      lastEditedAt
      lastEditedBy {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

export const SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE = gql(`
  mutation SetHarmonizedViolationStaffAssignee($tenantId: ID!, $id: ID!, $userId: ID) {
    setHarmonizedViolationStaffAssignee(tenantId: $tenantId, id: $id, userId: $userId) {
      id
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      lastEditedAt
      lastEditedBy {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

export const SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEES = gql(`
  mutation SetHarmonizedViolationStaffAssignees($tenantId: ID!, $ids: [ID!]!, $userId: ID) {
    setHarmonizedViolationStaffAssignees(tenantId: $tenantId, ids: $ids, userId: $userId) {
      id
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      lastEditedAt
      lastEditedBy {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

export const HARMONIZED_VIOLATION_STATS = gql(`
  query HarmonizedViolationStats($tenantId: ID!, $search: String) {
    harmonizedViolationStats(tenantId: $tenantId, search: $search) {
      total
      highSeverity
      unresolved
      stale
      facilitiesAffected
      bySeverity {
        key
        label
        count
      }
      byProgram {
        key
        label
        count
      }
      topFacilities {
        facilityId
        facilityName
        count
        byProgramSeverity {
          program
          severity
          count
        }
      }
      byQuarter {
        key
        label
        count
      }
      byAssignee {
        key
        label
        count
      }
      byRegion {
        key
        label
        count
        byProgramSeverity {
          program
          severity
          count
        }
      }
      byBusinessUnit {
        key
        label
        count
        byProgramSeverity {
          program
          severity
          count
        }
      }
      byProgramSeverity {
        program
        severity
        count
      }
      byProgramQuarter {
        key
        label
        program
        count
      }
      byAssigneeSeverity {
        key
        label
        severity
        count
      }
    }
  }
`);

export const LATEST_HARMONIZED_VIOLATION_REFRESH = gql(`
  query LatestHarmonizedViolationRefresh($tenantId: ID!) {
    latestHarmonizedViolationRefresh(tenantId: $tenantId) {
      id
      status
      startedAt
      finishedAt
    }
  }
`);

export const HARMONIZED_VIOLATION_COMMENTS = gql(`
  query HarmonizedViolationComments($tenantId: ID!, $harmonizedViolationId: ID!) {
    harmonizedViolationComments(tenantId: $tenantId, harmonizedViolationId: $harmonizedViolationId) {
      ...CommentThread
      isImportedFromAirtable
      govStatusChangedTo
    }
  }
`);

export const ADD_HARMONIZED_VIOLATION_COMMENT = gql(`
  mutation AddHarmonizedViolationComment(
    $tenantId: ID!
    $harmonizedViolationId: ID!
    $comment: String!
    $documentIds: [ID!]
  ) {
    addHarmonizedViolationComment(
      tenantId: $tenantId
      harmonizedViolationId: $harmonizedViolationId
      comment: $comment
      documentIds: $documentIds
    ) {
      ...CommentThread
      isImportedFromAirtable
      govStatusChangedTo
    }
  }
`);

export const DELETE_HARMONIZED_VIOLATION_COMMENT = gql(`
  mutation DeleteHarmonizedViolationComment($tenantId: ID!, $id: ID!) {
    deleteHarmonizedViolationComment(tenantId: $tenantId, id: $id)
  }
`);

export const HARMONIZED_VIOLATION_DOCUMENTS = gql(`
  query HarmonizedViolationDocuments($tenantId: ID!, $harmonizedViolationId: ID!) {
    harmonizedViolationDocuments(tenantId: $tenantId, harmonizedViolationId: $harmonizedViolationId) {
      id
      addedByCommentId
      deleted
      createdAt
      documentTitle
      documentFileExtension
      document {
        id
        title
        fileExtension
      }
    }
  }
`);

export const ATTACH_DOCUMENT_TO_HARMONIZED_VIOLATION = gql(`
  mutation AttachDocumentToHarmonizedViolation(
    $tenantId: ID!
    $harmonizedViolationId: ID!
    $documentId: ID!
  ) {
    attachDocumentToHarmonizedViolation(
      tenantId: $tenantId
      harmonizedViolationId: $harmonizedViolationId
      documentId: $documentId
    ) {
      id
      deleted
    }
  }
`);

export const DETACH_DOCUMENT_FROM_HARMONIZED_VIOLATION = gql(`
  mutation DetachDocumentFromHarmonizedViolation($tenantId: ID!, $id: ID!) {
    detachDocumentFromHarmonizedViolation(tenantId: $tenantId, id: $id) {
      id
      deleted
    }
  }
`);

export const VIOLATION_FIELD_NOTES_FRAGMENT = gql(`
  fragment ViolationFieldNotes on HarmonizedViolationFieldNotes {
    content
    writtenAt
    writtenBy {
      id
      email
      person {
        first
        last
      }
    }
  }
`);

/** Reads a violation's current Field Notes back out of the cache after a refetch. */
export const VIOLATION_CURRENT_FIELD_NOTES_FRAGMENT = gql(`
  fragment ViolationCurrentFieldNotes on HarmonizedViolation {
    id
    fieldNotes {
      ...ViolationFieldNotes
    }
  }
`);

export const EDIT_HARMONIZED_VIOLATION_FIELD_NOTES = gql(`
  mutation EditHarmonizedViolationFieldNotes(
    $tenantId: ID!
    $harmonizedViolationId: ID!
    $content: String!
    $basedOnWrittenAt: DateTime
  ) {
    editHarmonizedViolationFieldNotes(
      tenantId: $tenantId
      harmonizedViolationId: $harmonizedViolationId
      content: $content
      basedOnWrittenAt: $basedOnWrittenAt
    ) {
      ...ViolationFieldNotes
    }
  }
`);

export const REGENERATE_HARMONIZED_VIOLATION_FIELD_NOTES = gql(`
  mutation RegenerateHarmonizedViolationFieldNotes($tenantId: ID!, $harmonizedViolationId: ID!) {
    regenerateHarmonizedViolationFieldNotes(
      tenantId: $tenantId
      harmonizedViolationId: $harmonizedViolationId
    )
  }
`);

export const HARMONIZED_VIOLATION_FIELD_NOTES_GATHER_PREVIEW = gql(`
  query HarmonizedViolationFieldNotesGatherPreview(
    $tenantId: ID!
    $harmonizedViolationIds: [ID!]!
  ) {
    harmonizedViolationFieldNotesGatherPreview(
      tenantId: $tenantId
      harmonizedViolationIds: $harmonizedViolationIds
    ) {
      changedOrMissing
      unchanged
      writtenByPerson
      alreadyGathering
      skipped
    }
  }
`);

export const GATHER_HARMONIZED_VIOLATION_FIELD_NOTES = gql(`
  mutation GatherHarmonizedViolationFieldNotes(
    $tenantId: ID!
    $harmonizedViolationIds: [ID!]!
    $includeUnchanged: Boolean!
  ) {
    gatherHarmonizedViolationFieldNotes(
      tenantId: $tenantId
      harmonizedViolationIds: $harmonizedViolationIds
      includeUnchanged: $includeUnchanged
    )
  }
`);

export const STOP_HARMONIZED_VIOLATION_FIELD_NOTES = gql(`
  mutation StopHarmonizedViolationFieldNotes($tenantId: ID!, $harmonizedViolationId: ID!) {
    stopHarmonizedViolationFieldNotes(
      tenantId: $tenantId
      harmonizedViolationId: $harmonizedViolationId
    )
  }
`);

export const HARMONIZED_VIOLATION_FIELD_NOTES_STATUS = gql(`
  query HarmonizedViolationFieldNotesStatus($tenantId: ID!, $harmonizedViolationId: ID!) {
    harmonizedViolationFieldNotesStatus(
      tenantId: $tenantId
      harmonizedViolationId: $harmonizedViolationId
    ) {
      generating
      canRegenerate
      unchangedSinceGather
      current {
        ...ViolationFieldNotes
      }
    }
  }
`);
